/**
 * Pure CSV builders for the SRA results export (refactor Step 3).
 *
 * Extracted verbatim from App.js's onExportResultsCsv so the formatting logic
 * is unit-testable and exists exactly once. No fetching, no DOM — callers
 * fetch the rows and hand them in.
 */

/** Escape one CSV field per RFC 4180: quote when it contains , " or newline. */
export function escapeCsvField(v) {
  if (v == null) return '';
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

/**
 * Resolve the display answer for a results row: prefer answer_text, else parse
 * value_json ({ answer } / { text } / { value }, else the JSON itself).
 */
export function extractAnswerText(row) {
  let val = row.answer_text;
  if (val == null && row.value_json) {
    try {
      const parsed = typeof row.value_json === 'string' ? JSON.parse(row.value_json) : row.value_json;
      val = parsed.answer || parsed.text || parsed.value || JSON.stringify(parsed);
    } catch { val = String(row.value_json); }
  }
  return val;
}

/**
 * Build the results CSV string.
 *
 * @param rows  results rows ({ recipient_id, email, variable_name,
 *              answer_text, value_json, confidence, created_at })
 * @param opts.format  'long' (one row per variable per recipient — default) or
 *                     'wide' (one row per recipient, variables as columns)
 * @param opts.emailByRecipient  optional Map used when a row has no email
 * @returns csv string (no trailing newline)
 */
export function buildResultsCsv(rows, { format = 'long', emailByRecipient = new Map() } = {}) {
  const esc = escapeCsvField;

  if (format === 'wide') {
    // Wide format: one row per recipient, variables become columns
    const varNames = new Set();
    const byRecipient = {};
    for (const r of rows) {
      const rid = r.recipient_id || 'unknown';
      if (!byRecipient[rid]) byRecipient[rid] = { recipient_id: rid, email: r.email || emailByRecipient.get(rid) || '' };
      const vn = r.variable_name || '';
      varNames.add(vn);
      byRecipient[rid][vn] = extractAnswerText(r) || '';
    }
    const sortedVars = Array.from(varNames).sort();
    const header = ['recipient_id', 'email', ...sortedVars].map(esc).join(',');
    const dataRows = Object.values(byRecipient).map(rec =>
      [rec.recipient_id, rec.email, ...sortedVars.map(v => rec[v] || '')].map(esc).join(',')
    );
    return [header, ...dataRows].join('\n');
  }

  // Long format: one row per variable per recipient
  const header = ['recipient_id', 'email', 'variable_name', 'answer_text', 'confidence', 'created_at'].map(esc).join(',');
  const dataRows = rows.map(r => {
    const answerText = extractAnswerText(r);
    const email = r.email || emailByRecipient.get(r.recipient_id) || '';
    return [r.recipient_id, email, r.variable_name, answerText, r.confidence, r.created_at].map(esc).join(',');
  });
  return [header, ...dataRows].join('\n');
}
